import React from 'react';
import { Table, Button, Modal, Form, Input, InputNumber, Select, Checkbox, notification, Typography, Space, Tag } from 'antd';
import { PlusOutlined, ThunderboltOutlined, DownloadOutlined, PlusSquareOutlined, MinusSquareOutlined } from '@ant-design/icons';

import { useAuth } from '../../authentication/AuthProvider';
import request from '../../lib/request';
import { useBooks, BooksSelector, BooksCaption } from '../../lib/books';

const SIDES = ['Dr', 'Cr'];

/** Builds the tree the way the reference sheet lays it out: headings > accounts > sub-accounts (11101 under 1110). */
function buildTree(rows) {
  const byId = new Map(rows.map((r) => [r.id, { ...r, children: [] }]));
  const roots = [];
  for (const node of byId.values()) {
    const parent = node.parent_id ? byId.get(node.parent_id) : null;
    if (parent) parent.children.push(node);
    else roots.push(node);
  }
  const sort = (list) => {
    list.sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0) || String(a.code || '').localeCompare(String(b.code || '')));
    list.forEach((n) => (n.children.length ? sort(n.children) : delete n.children));
  };
  sort(roots);
  return roots;
}

/** Keeps every node that matches, plus its ancestors and descendants. */
function filterTree(nodes, matches) {
  const out = [];
  for (const n of nodes) {
    const kids = n.children ? filterTree(n.children, matches) : [];
    if (matches(n) || kids.length) out.push({ ...n, children: kids.length ? kids : n.children && matches(n) ? n.children : undefined });
  }
  return out;
}

function allIds(nodes, acc = []) {
  for (const n of nodes) {
    acc.push(n.id);
    if (n.children) allIds(n.children, acc);
  }
  return acc;
}

/** The chart of accounts of one set of books (or the ADMIN template, for a System Administrator). */
export default function ChartOfAccountsPage() {
  const auth = useAuth();
  const isSystemAdmin = auth.user.role === 'SYSTEM_ADMIN';
  const canImport = isSystemAdmin || auth.user.role === 'COMPANY_MANAGER';
  const { ledgerId, ledger } = useBooks();
  const [showTemplate, setShowTemplate] = React.useState(false);
  const [accounts, setAccounts] = React.useState([]);
  const [loading, setLoading] = React.useState(false);
  const [importing, setImporting] = React.useState(false);
  const [search, setSearch] = React.useState('');
  const [expandedKeys, setExpandedKeys] = React.useState([]);
  const [modalOpen, setModalOpen] = React.useState(false);
  const [isHeading, setIsHeading] = React.useState(false);
  const [form] = Form.useForm();

  const scope = showTemplate && isSystemAdmin ? { template: '1' } : ledgerId ? { ledger_id: ledgerId } : null;

  const fetchAccounts = () => {
    if (!scope) {
      setAccounts([]);
      return;
    }
    setLoading(true);
    request
      .get('/accounts', scope)
      .then((r) => r.json())
      .then((r) => {
        if (r.status) {
          setAccounts(r.data);
          // Open the top two levels (ASSETS > Non-Current Assets ...) by default.
          setExpandedKeys(r.data.filter((a) => a.is_heading && (!a.parent_id || r.data.find((p) => p.id === a.parent_id && !p.parent_id))).map((a) => a.id));
        } else notification.error({ message: r.message });
      })
      .finally(() => setLoading(false));
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  React.useEffect(fetchAccounts, [ledgerId, showTemplate]);

  const tree = React.useMemo(() => buildTree(accounts), [accounts]);
  const visible = React.useMemo(() => {
    if (!search) return tree;
    const q = search.toLowerCase();
    return filterTree(tree, (n) => String(n.code || '').includes(q) || n.name.toLowerCase().includes(q));
  }, [tree, search]);
  const shownExpanded = search ? allIds(visible) : expandedKeys;

  const handleBootstrap = () => {
    request
      .post('/accounts/bootstrap', {})
      .then((r) => r.json())
      .then((r) => {
        if (r.status) {
          notification.success({ message: `ADMIN template bootstrapped (${r.data.created.length} new row(s), ${r.data.updated.length} refreshed)` });
          fetchAccounts();
        } else notification.error({ message: r.message });
      });
  };

  const handleImportDefaults = () => {
    if (!ledgerId) return notification.error({ message: 'Choose the books first' });
    setImporting(true);
    request
      .post(`/ledgers/${ledgerId}/import-defaults`, {})
      .then((r) => r.json())
      .then((r) => {
        if (r.status) {
          notification.success({ message: `Chart of accounts: ${r.data.chartOfAccounts.created.length} new, ${r.data.chartOfAccounts.updated.length} refreshed from the ADMIN template.` });
          fetchAccounts();
        } else notification.error({ message: r.message });
      })
      .finally(() => setImporting(false));
  };

  const handleCreate = () => {
    form.validateFields().then((values) => {
      request
        .post('/accounts', { ...values, ...(scope.template ? { template: true } : { ledger_id: ledgerId }) })
        .then((r) => r.json())
        .then((r) => {
          if (r.status) {
            notification.success({ message: values.is_heading ? 'Heading created' : 'Account created' });
            setModalOpen(false);
            fetchAccounts();
          } else notification.error({ message: r.message });
        });
    });
  };

  const columns = [
    {
      title: 'Account',
      key: 'account',
      width: 460,
      className: 'coa-account-cell',
      render: (_, a) =>
        a.is_heading ? (
          <Typography.Text strong>{a.name}</Typography.Text>
        ) : (
          // Code and name are two flex cells, so a long name wraps within its own column instead of under the code.
          <span style={{ display: 'flex', alignItems: 'flex-start', flex: 1, minWidth: 0 }}>
            <Typography.Text type="secondary" style={{ flex: '0 0 64px', fontVariantNumeric: 'tabular-nums' }}>
              {a.code}
            </Typography.Text>
            <span style={{ flex: 1, minWidth: 0 }}>{a.name}</span>
          </span>
        ),
    },
    { title: 'Category 1', dataIndex: 'category_1', render: (v, a) => (v && !a.is_heading ? <Tag>{v}</Tag> : v || '') },
    { title: 'Category 2', dataIndex: 'category_2', render: (v, a) => (a.is_heading ? '' : v || '') },
    { title: 'Category 3', dataIndex: 'category_3', render: (v, a) => (a.is_heading ? '' : v || '') },
    {
      title: 'Reports',
      key: 'reports',
      render: (_, a) =>
        a.is_heading ? (
          ''
        ) : (
          <Space size={4} wrap>
            {a.shows_on_balance_sheet && <Tag color="blue">BS</Tag>}
            {a.shows_on_pl && <Tag color="purple">P&L</Tag>}
            {a.shows_on_far && <Tag color="orange">FAR</Tag>}
            {a.shows_on_ageing && <Tag color="gold">Ageing</Tag>}
          </Space>
        ),
    },
    { title: 'Depreciable?', dataIndex: 'has_depreciation', render: (v, a) => (v && !a.is_heading ? 'Yes' : '') },
    { title: 'Related Party?', dataIndex: 'is_related_party', render: (v, a) => (v && !a.is_heading ? 'Yes' : '') },
    { title: 'Increase', dataIndex: 'increase_side', render: (v, a) => (a.is_heading ? '' : v) },
    { title: 'Decrease', dataIndex: 'decrease_side', render: (v, a) => (a.is_heading ? '' : v) },
  ];

  const headings = accounts.filter((a) => a.is_heading);
  const parentOptions = accounts.map((a) => ({ value: a.id, label: a.is_heading ? a.name : `${a.code} ${a.name}` }));

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 16, gap: 16 }}>
        <div>
          <Typography.Title level={4} style={{ margin: 0 }}>
            Chart of Accounts
          </Typography.Title>
          {scope && scope.template ? <Tag color="purple">ADMIN template</Tag> : <BooksCaption />}
        </div>
        <Space wrap>
          <BooksSelector />
          {isSystemAdmin && <Checkbox checked={showTemplate} onChange={(e) => setShowTemplate(e.target.checked)}>ADMIN template</Checkbox>}
          <Button
            type="primary"
            icon={<PlusOutlined />}
            disabled={!scope}
            onClick={() => {
              form.resetFields();
              setIsHeading(false);
              setModalOpen(true);
            }}
          >
            New Account
          </Button>
        </Space>
      </div>

      <Space style={{ marginBottom: 16 }} wrap>
        {isSystemAdmin && showTemplate && (
          <Button icon={<ThunderboltOutlined />} onClick={handleBootstrap}>
            Bootstrap ADMIN Template
          </Button>
        )}
        {canImport && !showTemplate && ledger && (
          <Button icon={<DownloadOutlined />} onClick={handleImportDefaults} loading={importing}>
            Import template rows into these books
          </Button>
        )}
        <Input.Search placeholder="Search code or name" allowClear style={{ width: 240 }} onChange={(e) => setSearch(e.target.value)} />
        <Button icon={<PlusSquareOutlined />} onClick={() => setExpandedKeys(allIds(tree))}>
          Expand All
        </Button>
        <Button icon={<MinusSquareOutlined />} onClick={() => setExpandedKeys(tree.map((n) => n.id))}>
          Collapse
        </Button>
        {headings.length === 0 && accounts.length > 0 && (
          <Typography.Text type="warning">This chart has no headings yet - import the template rows again to arrange it as a tree.</Typography.Text>
        )}
      </Space>

      <Table
        rowKey="id"
        size="small"
        columns={columns}
        dataSource={visible}
        loading={loading}
        pagination={false}
        scroll={{ x: 1180 }}
        locale={{ emptyText: scope ? 'No accounts' : 'Choose the books first' }}
        expandable={{ expandedRowKeys: shownExpanded, onExpandedRowsChange: (keys) => !search && setExpandedKeys(keys), indentSize: 22 }}
        rowClassName={(a) => (a.is_heading ? 'coa-heading-row' : '')}
      />

      <Modal title={isHeading ? 'New Heading' : 'New Account'} open={modalOpen} onCancel={() => setModalOpen(false)} onOk={handleCreate}>
        <Form form={form} layout="vertical" onValuesChange={(c) => c.is_heading !== undefined && setIsHeading(c.is_heading)}>
          <Form.Item name="is_heading" valuePropName="checked">
            <Checkbox>This is a heading (groups accounts, has no code)</Checkbox>
          </Form.Item>
          <Form.Item name="parent_id" label="Under" tooltip="The heading (or account, for a sub-account such as 11101 under 1110) this belongs to">
            <Select allowClear showSearch optionFilterProp="label" options={parentOptions} />
          </Form.Item>
          <Form.Item name="code" label="Code" rules={[{ required: !isHeading }]} hidden={isHeading}>
            <InputNumber style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item name="name" label="Name" rules={[{ required: true }]}>
            <Input />
          </Form.Item>
          <Form.Item name="category_1" label="Category 1">
            <Input />
          </Form.Item>
          <Form.Item name="category_2" label="Category 2">
            <Input />
          </Form.Item>
          <Form.Item name="category_3" label="Category 3">
            <Input />
          </Form.Item>
          {!isHeading && (
            <>
              <Form.Item name="shows_on_balance_sheet" valuePropName="checked">
                <Checkbox>Balance Sheet</Checkbox>
              </Form.Item>
              <Form.Item name="shows_on_pl" valuePropName="checked">
                <Checkbox>Profit &amp; Loss</Checkbox>
              </Form.Item>
              <Form.Item name="shows_on_far" valuePropName="checked">
                <Checkbox>Fixed Asset Register</Checkbox>
              </Form.Item>
              <Form.Item name="shows_on_ageing" valuePropName="checked">
                <Checkbox>Ageing</Checkbox>
              </Form.Item>
              <Form.Item name="has_depreciation" valuePropName="checked">
                <Checkbox>Depreciable</Checkbox>
              </Form.Item>
              <Form.Item name="is_related_party" valuePropName="checked">
                <Checkbox>Related party</Checkbox>
              </Form.Item>
              <Space>
                <Form.Item name="increase_side" label="Increase">
                  <Select allowClear style={{ width: 100 }} options={SIDES.map((s) => ({ value: s, label: s }))} />
                </Form.Item>
                <Form.Item name="decrease_side" label="Decrease">
                  <Select allowClear style={{ width: 100 }} options={SIDES.map((s) => ({ value: s, label: s }))} />
                </Form.Item>
              </Space>
            </>
          )}
        </Form>
      </Modal>
      <style>{`
        .coa-heading-row td { background: #fafafa; }
        /* indent + expand icon + (code | name) laid out as one flex row, so long names wrap inside the name column */
        td.coa-account-cell { display: flex; align-items: flex-start; }
        td.coa-account-cell .ant-table-row-indent { flex: 0 0 auto; }
        td.coa-account-cell .ant-table-row-expand-icon { flex: 0 0 auto; margin-top: 3px; }
        td.coa-account-cell .ant-table-row-expand-icon-spaced { flex: 0 0 auto; }
      `}</style>
    </div>
  );
}
